(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.ICeFinderCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const DEFAULT_RULES = Object.freeze({
    // 48 cm is the folded scooter height published in the S1/S2 product FAQ.
    // Do not extend it using the owner's separate length allowance or ±2 cm flat-bag tolerance.
    S1: { nominal: 112, maxLength: 118, requiresHandlebarWidth: true, maxHandlebarWidthCm: 43, requiresFoldedHeight: true, maxFoldedHeightCm: 48, category: 'home' },
    S2: { nominal: 125, maxLength: 128, requiresHandlebarWidth: true, maxHandlebarWidthCm: 65, requiresFoldedHeight: true, maxFoldedHeightCm: 48, category: 'home' },
    S3: { nominal: 112, margin: 0, category: 'transport' },
    S4: { nominal: 140, margin: 0, category: 'transport' },
    S5: { nominal: 131, margin: 15, maxConfirmed: 145, category: 'home' }
  });
  function normalize(value) {
    return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().replace(/\+/g, 'plus').replace(/[^a-z0-9]/g, '');
  }
  function search(models, query, limit = 6) {
    const words = String(query || '').trim().split(/\s+/).map(normalize).filter(Boolean);
    if (!words.length || normalize(query).length < 2) return [];
    return models.filter(m => {
      const fields = [m.brand, m.name, ...(m.aliases || [])].join(' ');
      const all = normalize(fields);
      return words.every(w => all.includes(w));
    }).sort((a, b) => a.label.localeCompare(b.label)).slice(0, limit);
  }
  function dimension(value) {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const n = Number(String(value).replace(',', '.'));
    return Number.isFinite(n) && n >= 40 && n <= 250 ? n : null;
  }
  function handlebarWidth(value) {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const n = Number(String(value).replace(',', '.'));
    return Number.isFinite(n) && n >= 10 && n <= 150 ? n : null;
  }
  function foldedHeight(value) {
    return handlebarWidth(value); // Same input range, but a distinct physical measurement.
  }
  function findExact(models, query) {
    const key = normalize(query);
    if (key.length < 2) return null;
    const matches = models.filter(m => normalize(m.label) === key || normalize(m.name) === key);
    return matches.length === 1 ? matches[0] : null;
  }
  function referenceLength(model) {
    // Store-owner rule: use the guide's folded length as the selection reference
    // for BOTH uses. This is not a separately measured unfolded dimension.
    return model ? dimension(model.foldedMax) : null;
  }
  function webSearchUrl(query, locale = 'es') {
    const terms = {es:'patinete eléctrico medidas largo ficha técnica fabricante',en:'electric scooter length dimensions official manufacturer',fr:'trottinette électrique longueur dimensions fiche fabricant',de:'E-Scooter Länge Maße Hersteller technische Daten'};
    const model = String(query || '').trim().slice(0,160);
    if (normalize(model).length < 2) return null;
    return 'https://www.google.com/search?q=' + encodeURIComponent(model + ' ' + (terms[locale] || terms.en));
  }
  function maxLength(rule) {
    if (rule.maxLength !== undefined) return dimension(rule.maxLength) ?? 0;
    if (rule.category === 'transport') return rule.nominal;
    const calculated = Math.floor(rule.nominal * (1 + rule.margin / 100) * 100 + 1e-7) / 100;
    return rule.maxConfirmed ? Math.min(calculated, rule.maxConfirmed) : calculated;
  }
  function recommend(input, rules = DEFAULT_RULES) {
    const use = ['home', 'transport', 'both'].includes(input.use) ? input.use : 'home';
    const reference = input.length !== undefined ? dimension(input.length) : referenceLength(input.model);
    const lengths = { home: dimension(input.folded) ?? reference, transport: dimension(input.unfolded) ?? reference };
    const categories = use === 'both' ? ['home', 'transport'] : [use];
    const missing = categories.filter(c => lengths[c] === null);
    const width = input.handlebarWidthCm !== undefined ? handlebarWidth(input.handlebarWidthCm) : handlebarWidth(input.model?.handlebarWidthCm);
    const height = input.foldedHeightCm !== undefined ? foldedHeight(input.foldedHeightCm) : foldedHeight(input.model?.foldedHeightCm);
    // Owner-confirmed routing for handlebars wider than 65 cm. Length caps still apply.
    const wideHandlebar = width !== null && width > 65;
    const pending = [];
    const rejected = [];
    const excluded = input.model?.fitExclusions || [];
    const items = Object.entries(rules).filter(([, r]) => categories.includes(r.category))
      .flatMap(([id, rule]) => {
        const length = lengths[rule.category];
        const limit = maxLength(rule);
        if (length === null) return [];
        const candidate = { id, category: rule.category, length, limit,
          flexible: length > rule.nominal, nearLimit: limit - length < 2, wideHandlebar };
        const reject = reason => { rejected.push({...candidate,reason}); return []; };
        if (length > limit) return reject('length');
        if (excluded.includes(id)) return reject('physical-exclusion');
        if (wideHandlebar && !['S5','S4'].includes(id)) return reject('width');
        const reasons = [], missingMeasurements = [];
        if (rule.requiresHandlebarWidth) {
          const capacity = handlebarWidth(rule.maxHandlebarWidthCm);
          if (capacity !== null && width !== null && width > capacity) return reject('width');
          if (capacity === null) reasons.push('bag-width-unconfirmed');
          if (width === null) { reasons.push('model-width-missing'); missingMeasurements.push('width'); }
          if (width !== null) candidate.handlebarWidthCm = width;
        }
        if (rule.requiresFoldedHeight) {
          const capacity = foldedHeight(rule.maxFoldedHeightCm);
          if (capacity !== null && height !== null && height > capacity) return reject('height');
          if (capacity === null) reasons.push('bag-height-unconfirmed');
          if (height === null) { reasons.push('model-height-missing'); missingMeasurements.push('height'); }
          if (height !== null) candidate.foldedHeightCm = height;
        }
        if (reasons.length) {
          pending.push({...candidate,reason:reasons[0],reasons,missingMeasurements});
          return [];
        }
        return [candidate];
      });
    // Availability never changes whether a product is compatible.
    const priority = ['S5', 'S1', 'S2', 'S3', 'S4'];
    items.sort((a, b) => priority.indexOf(a.id) - priority.indexOf(b.id));
    return { items, pending, rejected, missing, empty: categories.filter(c => !missing.includes(c) && !items.some(i => i.category === c) && !pending.some(i => i.category === c)) };
  }
  return { DEFAULT_RULES, normalize, search, dimension, handlebarWidth, foldedHeight, findExact, referenceLength, webSearchUrl, maxLength, recommend };
});
